// src/clock/stuck.js — stuck-session detection + resolution UI (v88.0)
//
// Extracted from index.html in v88.0. Previously lived at lines 5973–6153
// in v87.1. Owns the "you forgot to clock out from a past day" flow:
//
//   - On clock-screen entry, query DB for open sessions on past dates
//   - If any exist, paint a red banner in #stillClockedInBannerHolder
//   - Banner tap → modal pre-loaded with the oldest open session
//   - User picks a clock-out time → DB.clock.save with that timestamp
//   - Modal advances through remaining sessions until none left
//
// Why this is its own module (not part of action.js): it's a recovery
// flow, not a clock-in/out flow. Different DB endpoint (openSessions),
// different UI surface (red persistent banner, dedicated modal with
// per-session iteration), different state (the oldest-first queue
// lives only in this module — it's not part of clockEvents).
//
// Module-private state:
//   _stuckSessions     — array of clock_log rows pending resolution
//   _stuckSessionActive — the row currently bound to the modal inputs
//
// Both are intentionally module-private — no consumer outside stuck.js
// should be poking at them. Exporting them would invite divergent
// resolution paths (e.g. admin.html mutating the queue) and the
// invariants are easier to reason about with one writer.
//
// Free-variable dependencies (resolved from window at call time):
//   currentUser, currentScreen, DB, ALL_STAFF, getMonday, localDateStr,
//   getDayKey, showToast, dlog. All live as inline globals in index.html.
//
// Inline onclicks: renderStuckBanner generates HTML containing
// `onclick="Clock.openStuckModal()"`. The Clock.X form is required
// because bare names aren't on window after the IIFE bundle wraps the
// module — only the Clock namespace export reaches global scope.
//
// CATEGORY 1 — DELIBERATE PRODUCT RULES (do not "simplify"):
//
//   Area managers + admins are skipped. They have no personal clock
//   data and manage open sessions through the admin UI instead. The
//   skip happens at the top of checkStillClockedIn — don't move it
//   downstream.
//
//   Modal advances oldest-first. The pop order is _stuckSessions.pop()
//   semantics (resolve from the tail of a desc-sorted array), so the
//   first session the user sees is the OLDEST stuck shift. This is
//   deliberate — old stuck rows are the ones most likely to be
//   forgotten and most likely to corrupt weekly hour totals.
//
// CATEGORY 2 — DEFENSIVE LOGIC FROM OBSERVED BUGS:
//
//   v65: Filter out open sessions where the staff was scheduled D/O
//        that day. A spurious clock_in on a day-off is technically a
//        data issue, but presenting it as "you forgot to clock out"
//        confused users (Joyceline at Dubai Hills, 2026-04-27). The
//        filter only drops EXPLICIT D/O matches — no-shift-entry rows
//        still surface as stuck. If the shift fetch itself fails, we
//        fall through showing all sessions (old behaviour).

import { clockEvents, mergeCarriedOpenShifts } from './state.js';   // v129.152: clockEvents read for the repaint-on-change signature
import { renderSingleStoreClock } from './render.js';
import { getClockResetGen } from './state.js';   // v136.8: the throttle never skips after an unanswered reset
import '../shared/key-activate.js';   // v136.8: Enter/Space on the role="button" banners here and in location.js

// Module-private state — see header comment for rationale on not exporting.
var _stuckSessions = [];     // array of clock_log rows to resolve
var _stuckSessionActive = null; // row currently being resolved in the modal

// v136.8 (the 16 Oct render audit): THE THROTTLE, and what it must never break.
//
// Every clock-screen draw used to call checkStillClockedIn, and a sign-in draws
// that screen three times plus the deferred login check: four open-sessions
// reads in the first second, on every till, every sign-in. The draws now call
// checkStuckThrottled, which reads again only when:
//   - the last check started 60 s or more ago, or was for another store, or
//     failed (a failure clears the stamp, so the next draw tries again, as before);
//   - OR clockEvents were reset by a load of today (getClockResetGen moved) and
//     no check has answered since. That reset also wipes a shift CARRIED from
//     the previous business day (the overnight crew's Clock Out card); only this
//     check puts it back, so a reset must always be answered by a fresh check.
// The FORCED paths are unchanged and call checkStillClockedIn directly: sign-in,
// the 5-minute interval. The date rollover and every clock action call
// forceNextStuckCheck(), so the next draw reads again whatever the clock says.
var STUCK_THROTTLE_MS = 60000;
var _stuckLastAt = 0;        // when the last check STARTED (0 = the next draw reads again)
var _stuckLastStore = null;  // the store it was for
var _stuckInFlight = 0;      // checks started and not yet answered
var _stuckAppliedGen = -1;   // getClockResetGen() when the last answer was applied

export function forceNextStuckCheck() { _stuckLastAt = 0; }

export function checkStuckThrottled() {
  if (!currentUser || !currentUser.store) return checkStillClockedIn();   // its own early returns, no request
  var fresh = _stuckLastAt > 0 && (Date.now() - _stuckLastAt) < STUCK_THROTTLE_MS && _stuckLastStore === currentUser.store;
  // A check still in flight answers any reset that happened before it lands.
  var answered = _stuckInFlight > 0 || _stuckAppliedGen === getClockResetGen();
  if (fresh && answered) return Promise.resolve();
  return checkStillClockedIn();
}

// v130.41 — is this rota row somebody rostered to WORK? Mirrors the worked
// types the close-stale tool already recognises (src/worker/routes/clock-stale.js).
// A day with no worked leg is a real day off; a day with one is not, however
// many 'off' rows sit beside it.
// v136.8: the rota week a date belongs to AT THIS STORE. Qatar weeks start on
// Sunday; keying by Monday never matched a Qatar rota row, so a session left
// open on a rostered day off still raised the banner there.
function stuckWeekStart(d) {
  var store = currentUser && currentUser.store;
  var qatar = typeof isQatarStore === 'function' && isQatarStore(store);
  return (qatar && typeof getSunday === 'function') ? getSunday(d) : getMonday(d);
}

export function isWorkedShift(s) {
  return !!(s && (s.type === 'work' || s.type === 'atstore'));
}

export async function checkStillClockedIn() {
  if (!currentUser) return;
  // Area managers / admins skip — they manage open sessions via admin UI
  if (currentUser.role === 'area_manager' || currentUser.role === 'admin') return;
  if (!currentUser.store) return;
  _stuckLastAt = Date.now();          // v136.8: stamped at the START, so draws meanwhile do not ask again
  _stuckLastStore = currentUser.store;
  _stuckInFlight++;
  try {
    var rows = await DB.clock.openSessions({ store: currentUser.store });
    rows = Array.isArray(rows) ? rows : [];

    // v65: filter out sessions where the staff was scheduled D/O on that
    // date. A spurious clock_in on a scheduled day-off is technically a data
    // integrity issue, but surfacing it as "you forgot to clock out" causes
    // user confusion (Joyceline at Dubai Hills, 2026-04-27). Conservative
    // filter: only drops sessions where there's an EXPLICIT D/O shift —
    // missing shift entries are still surfaced as before.
    if (rows.length > 0) {
      var weekKeys = {};
      rows.forEach(function(r){
        try {
          var d = new Date(r.date + 'T00:00:00');
          var wk = localDateStr(stuckWeekStart(d));
          if (!weekKeys[wk]) weekKeys[wk] = true;
        } catch(e) {}
      });
      var weeks = Object.keys(weekKeys);
      try {
        var allShifts = await Promise.all(weeks.map(function(wk){
          return DB.shifts.list({ week: wk, store: currentUser.store }).catch(function(){ return []; });
        }));
        // v130.41 — a day can hold MORE THAN ONE rota row since split shifts
        // (v129.153 put `seq` on `shifts`). This map used to keep ONE row per
        // (week, staff, day) — whichever came last out of PostgREST — so on a
        // two-leg day whether a stuck session was hidden depended on row
        // ORDER, not on what the person was rostered to do. Collect them all
        // and judge the DAY, the same move v129.154 made for the pay maths.
        var shiftLookup = {};
        allShifts.forEach(function(arr, i){
          if (!Array.isArray(arr)) return;
          var wk = weeks[i];
          arr.forEach(function(s){
            var k = wk + '_' + s.staff_id + '_' + s.day;
            (shiftLookup[k] = shiftLookup[k] || []).push(s);
          });
        });
        var droppedCount = 0;
        rows = rows.filter(function(r){
          try {
            var d = new Date(r.date + 'T00:00:00');
            var wkKey = localDateStr(stuckWeekStart(d));
            var dayKey = getDayKey(d);
            var sh = shiftLookup[wkKey + '_' + r.staff_id + '_' + dayKey];
            // Only filter on explicit D/O. No-shift-entry stays visible.
            //
            // v130.41 — "explicit D/O" now means the WHOLE day is off. If any
            // leg of the day is a worked row, the person was rostered to work
            // and a session left open is a genuine forgot-to-clock-out, not
            // the v65 spurious-clock-in-on-a-day-off case. A single 'off' row
            // beside a worked leg must never hide it.
            if (sh && sh.length && !sh.some(isWorkedShift)) { droppedCount++; return false; }
          } catch(e) {}
          return true;
        });
        if (droppedCount > 0) {
          dlog('[Stuck] Filtered ' + droppedCount + ' open session(s) for staff scheduled D/O');
        }
      } catch(e) {
        // Shift fetch failed — fall through showing all sessions (old behaviour)
        console.warn('[Stuck] Shift filter failed, showing all sessions:', e && e.message);
      }
    }

    // v123.x — carry still-open shifts from the PREVIOUS business day into the
    // normal clock cards (so a late finisher can clock out as usual after the
    // 3am roll-over). Keep those OUT of the forgot-to-clock-out banner so they
    // aren't surfaced twice.
    // v129.148 — now bounded by the SHIFT'S AGE (12h), not the wall clock. The
    // old `new Date().getHours()` gate closed at 06:00 and a 22:00-07:00
    // restock finishes at 07:00, so the crew could never clock out.
    // v129.152 — REPAINT ONLY ON CHANGE (the v125.97 click-eating class).
    // Snapshot who was already carried BEFORE the merge: a re-run that merely
    // re-classifies an existing carried entry must NOT rebuild the cards.
    // With v129.151 reviving checkStuck at three staggered call sites (login,
    // the auth render chain, the Clock tab), an unconditional repaint fired
    // repeatedly in the first seconds on screen — replacing the card nodes
    // mid-tap, so a crew member's Clock Out tap could land on a dead node.
    // The overnight e2e caught it: Playwright's click retried through 10s of
    // churn. Same cure as the chat poll (_threadSig): don't rebuild what
    // didn't change.
    var preCarried = {};
    Object.keys(clockEvents).forEach(function(k){
      var ev = clockEvents[k];
      if (ev && ev.carriedOver && (ev.status === 'in' || ev.status === 'break')) preCarried[k] = true;
    });
    var carried = mergeCarriedOpenShifts(rows, Date.now());
    _stuckAppliedGen = getClockResetGen();   // v136.8: this answer covers every reset up to now
    _stuckSessions = rows.filter(function(r){ return !carried[parseInt(r.staff_id)]; });
    // If the merge introduced a NEWLY carried shift, repaint the clock cards so
    // it shows. (renderSingleStoreClock does NOT call back into checkStuck —
    // no loop.)
    var newlyCarried = Object.keys(carried).some(function(id){ return !preCarried[id]; });
    if (newlyCarried && currentScreen === 'clock' && typeof renderSingleStoreClock === 'function') {
      renderSingleStoreClock();
    }
  } catch (e) {
    console.warn('[Stuck] Open-sessions check failed:', e && e.message);
    _stuckSessions = [];
    _stuckLastAt = 0;   // v136.8: a failed check is tried again on the next draw, as before the throttle
  }
  _stuckInFlight--;
  renderStillClockedInBanner();
}

// Look up a staff name from the local ALL_STAFF array by id. Returns "Staff #<id>" if not found.
// Module-private — only the modal needs this and it's a thin helper.
function _stuckStaffName(staffId) {
  if (!Array.isArray(ALL_STAFF)) return 'Staff #' + staffId;
  var s = ALL_STAFF.find(function(x){ return String(x.id) === String(staffId); });
  return s ? s.name : ('Staff #' + staffId);
}

export function renderStillClockedInBanner() {
  var holder = document.getElementById('stillClockedInBannerHolder');
  if (!holder) return;
  if (currentScreen !== 'clock') { holder.innerHTML = ''; return; }
  if (!_stuckSessions || _stuckSessions.length === 0) { holder.innerHTML = ''; return; }
  var n = _stuckSessions.length;
  var titleText = n === 1
    ? '1 open shift from a past day'
    : n + ' open shifts from past days';
  // v130.1 — NAME the person and the DAY (Raha Mall, 16 Aug): a staffer read
  // the dateless "Someone forgot to clock out" as their PREVIOUS shift having
  // failed and reported it as a bug, when the row was actually days old.
  // The oldest session sits last in the desc-ordered list — same pick as
  // openStuckModal, so the banner names the shift the modal will open with.
  var oldest = _stuckSessions[_stuckSessions.length - 1];
  var who = _stuckStaffName(oldest.staff_id);
  var when = oldest.date;
  try {
    when = new Date(oldest.date + 'T00:00:00')
      .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch (e) {}
  var subText = n === 1
    ? escapeHtml(who) + ' on ' + escapeHtml(when) + '. Tap to review and close.'
    : 'Oldest: ' + escapeHtml(who) + ' on ' + escapeHtml(when) + '. Tap to review and close.';
  holder.innerHTML =
    '<div class="stuck-banner" role="button" tabindex="0" onclick="Clock.openStuckModal()">'+
      '<span class="stuck-banner-icon">⚠️</span>'+
      '<div class="stuck-banner-body">'+
        '<div class="stuck-banner-title">'+titleText+'</div>'+
        '<div class="stuck-banner-sub">'+subText+'</div>'+
      '</div>'+
    '</div>';
}

// Open modal with the oldest open session pre-loaded.
// Resolving one advances to the next; if none remain, closes modal and clears banner.
export function openStuckModal() {
  if (!_stuckSessions || _stuckSessions.length === 0) return;
  _stuckSessionActive = _stuckSessions[_stuckSessions.length - 1]; // oldest (ordered desc)
  var row = _stuckSessionActive;
  var pretty = row.date;
  try {
    var d = new Date(row.date + 'T00:00:00');
    pretty = d.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'});
  } catch(e){}
  // Show a "N remaining" hint in the head if more than one
  var remaining = _stuckSessions.length;
  var headEl = document.querySelector('#stuckModal .stuck-head h3');
  if (headEl) {
    headEl.textContent = remaining > 1
      ? '🕐 Clock out from a past shift (' + remaining + ' to review)'
      : '🕐 Clock out from a past shift';
  }
  document.getElementById('stuckDate').textContent = pretty;
  // Replace the "date" row's label briefly — actually keep date, but add staff name as a new row above if possible.
  // Simpler: prepend staff name into the Date line (since this is a shared device login).
  var staffLine = _stuckStaffName(row.staff_id);
  var dateCell = document.getElementById('stuckDate');
  if (dateCell) dateCell.innerHTML = '<div style="margin-bottom:2px" translate="no">'+staffLine+'</div><div style="font-size:12px;color:var(--sub);font-weight:500">'+pretty+'</div>';
  // clock_in looks like "YYYY-MM-DDTHH:MM:SS" — show just the HH:MM
  var ci = row.clock_in || '';
  var timeOnly = ci.length >= 16 ? ci.substring(11, 16) : ci;
  document.getElementById('stuckClockIn').textContent = timeOnly || '—';
  // Default out-time to 22:00 (standard store close) — user can edit
  document.getElementById('stuckClockOutTime').value = '22:00';
  document.getElementById('stuckSaveBtn').disabled = false;
  document.getElementById('stuckModal').classList.add('open');
}

export function closeStuckModal() {
  document.getElementById('stuckModal').classList.remove('open');
  _stuckSessionActive = null;
}

// Click backdrop to close — attach once on module load. Matches the pattern
// used in detail.js and location.js (IIFE at module bottom).
(function(){
  var m = document.getElementById('stuckModal');
  if (m) m.addEventListener('click', function(e){ if (e.target === m) closeStuckModal(); });
})();

export async function submitStuckResolve() {
  if (!_stuckSessionActive) return;
  var row = _stuckSessionActive;
  var timeInput = document.getElementById('stuckClockOutTime').value; // "HH:MM"
  if (!timeInput || !/^\d{2}:\d{2}$/.test(timeInput)) {
    showToast('Please enter a valid time', 'error');
    return;
  }
  var btn = document.getElementById('stuckSaveBtn');
  btn.disabled = true;
  // Build the clock_out timestamp on the clock-in's date. v132.86 (overnight
  // audit): a finish EARLIER than the clock-in is an overnight shift, so it
  // goes on the next day, as the admin amend screen already does. It used to
  // be stamped on the start date, which the server rejects as "clock_out
  // earlier than clock_in", leaving a 22:00-07:00 shift impossible to close.
  var ciStr = String(row.clock_in || '');
  var baseDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(ciStr) ? ciStr.slice(0, 10) : row.date;
  var outDate = baseDate;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(ciStr) && timeInput < ciStr.slice(11, 16)) {
    var nd = new Date(baseDate + 'T12:00:00Z');
    nd.setUTCDate(nd.getUTCDate() + 1);
    outDate = nd.toISOString().slice(0, 10);   // date arithmetic only, at UTC noon: no timezone drift
  }
  var clockOutTs = outDate + 'T' + timeInput + ':00';
  // Upsert via DB.clock.save: the endpoint upserts by (staff_id, date, seq)
  try {
    await DB.clock.save({
      staff_id: row.staff_id,
      date: row.date,
      // v132.86: the session this row IS. Without it the server treated the
      // save as session 1, so closing a stuck SECOND shift of a split day
      // overwrote the closed morning session and left the night one open.
      seq: row.seq || 1,
      clock_in: row.clock_in,
      clock_out: clockOutTs,
      break_start: row.break_start || null,
      break_mins: row.break_mins || 0,
      store: row.store || (currentUser && currentUser.store) || null,
    });
    showToast('✅ Shift closed for ' + _stuckStaffName(row.staff_id), 'success');
    // Remove this row from the local stuck list
    _stuckSessions = _stuckSessions.filter(function(r){
      // v132.86: the same SESSION, not every row that day.
      return !(String(r.staff_id) === String(row.staff_id) && r.date === row.date && (r.seq || 1) === (row.seq || 1));
    });
    if (_stuckSessions.length > 0) {
      // Advance to the next one — keep modal open so the user can plough through
      openStuckModal();
    } else {
      closeStuckModal();
    }
    renderStillClockedInBanner();
  } catch (e) {
    btn.disabled = false;
    showToast('Save failed: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
}
